import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import {
  Observable,
  forkJoin,
  map,
  of,
  switchMap,
  catchError,
  throwError,
} from 'rxjs';
import { IcrudService } from './crud.models';
import { environment } from 'src/environments/environment';
import { ApiError } from '../error-handling/error.models';

@Injectable({
  providedIn: 'root',
})
export class CrudService<Type> implements IcrudService<Type> {
  private baseUrl: string = environment.apiUrl + '/api/';

  constructor(private http: HttpClient) {}

  getAll(
    apiName: string,
    page: number = 1,
    itemsPerPage: number = 10,
    filters: any = {},
    relations: string[] = [],
    optionalQueryParams: string | null = null,
    context?: HttpContext
  ): Observable<any | ApiError> {
    const params = this.buildQueryParams(page, itemsPerPage, filters);

    let url = `${this.baseUrl}${apiName}?${params}`;

    if (optionalQueryParams) {
      url = `${this.baseUrl}${apiName}?${optionalQueryParams}&${params}`;
    }
    return this.http.get<any | ApiError>(url, { context }).pipe(
      switchMap((response) => {
        if (relations.length > 0) {
          return this.enrichWithRelations(response, relations);
        }
        return of(response);
      })
    );
  }

  getById(
    apiName: string,
    id: string,
    relations: string[] = []
  ): Observable<Type | ApiError> {
    const url = `${this.baseUrl}${apiName}/${id}`;

    return this.http.get<Type | ApiError>(url).pipe(
      switchMap((response) => {
        if (relations.length > 0) {
          return this.enrichWithRelations(response, relations);
        }
        return of(response);
      })
    );
  }

  create(
    apiName: string,
    data: any,
    optionalQueryParams: string | null = null,
    context?: HttpContext
  ): Observable<Type | ApiError> {
    const url = optionalQueryParams
      ? `${this.baseUrl}${apiName}?${optionalQueryParams}`
      : `${this.baseUrl}${apiName}`;
    return this.http.post<Type | ApiError>(url, data, { context });
  }

  update(
    apiName: string,
    data: any,
    optionalQueryParams: string | null = null,
    context?: HttpContext
  ): Observable<Type | ApiError> {
    const url = data.id
      ? `${this.baseUrl}${apiName}/${data.id}`
      : `${this.baseUrl}${apiName}`;
    const urlWithParams = optionalQueryParams
      ? `${url}?${optionalQueryParams}`
      : url;
    return this.http.patch<Type | ApiError>(urlWithParams, data, { context });
  }

  updateWithNoIdCheck(apiName: string, data: any): Observable<Type | ApiError> {
    const url = `${this.baseUrl}${apiName}`;
    return this.http.patch<Type | ApiError>(url, data);
  }

  updateAction(
    apiName: string,
    data: any,
    action: string
  ): Observable<Type | ApiError> {
    const url = data.id
      ? `${this.baseUrl}${apiName}/${data.id}/${action}`
      : `${this.baseUrl}${apiName}`;
    return this.http.patch<Type | ApiError>(url, data);
  }

  createAction(
    apiName: string,
    data: any,
    action: string
  ): Observable<Type | ApiError> {
    const url = data.id
      ? `${this.baseUrl}${apiName}/${data.id}/${action}`
      : `${this.baseUrl}${apiName}`;
    return this.http.post<Type | ApiError>(url, data);
  }

  getAction(
    apiName: string,
    data: any,
    action: string
  ): Observable<Type | ApiError> {
    const url = data.id
      ? `${this.baseUrl}${apiName}/${data.id}/${action}`
      : `${this.baseUrl}${apiName}`;
    return this.http.get<Type | ApiError>(url);
  }

  delete(apiName: string, id?: string): Observable<any | ApiError> {
    const url = `${this.baseUrl}${apiName}${id ? `/${id}` : ''}`;
    return this.http.delete<any | ApiError>(url);
  }

  private buildQueryParams(
    page: number,
    itemsPerPage: number,
    filters: any
  ): string {
    let params = `page=${page}&itemsPerPage=${itemsPerPage}`;

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (Array.isArray(value)) {
        // Pour tous les tableaux, utiliser le format avec []
        // Format attendu: userRole[]=value1&userRole[]=value2
        value.forEach((val) => {
          params += `&${key}[]=${encodeURIComponent(val)}`;
        });
      } else if (value !== undefined && value !== null && value !== '') {
        // Ne pas exclure les valeurs falsy valides comme false ou 0
        params += `&${key}=${encodeURIComponent(value)}`;
      }
    });

    return params;
  }

  private enrichWithRelations(data: any, relations: string[]): Observable<any> {
    const collectionKey = Array.isArray(data?.member)
      ? 'member'
      : Array.isArray(data?.['hydra:member'])
      ? 'hydra:member'
      : null;
    const components = collectionKey ? data[collectionKey] : [data];

    if (!components || components.length === 0) {
      return of(data);
    }

    const enrichedComponents: Observable<any>[] = components.map(
      (component: any) => {
        const relationRequests: Observable<any>[] = relations.map(
          (relation) => {
            const relationRef = this.extractRelationUrl(component[relation]);
            if (relationRef) {
              return this.http
                .get<any>(this.toAbsoluteApiUrl(relationRef))
                .pipe(map((relationData) => ({ [relation]: relationData })));
            }
            return of({});
          }
        );

        return forkJoin(relationRequests).pipe(
          map((relationDataArray: any[]) => {
            const relationsData = Object.assign({}, ...relationDataArray);
            return { ...component, ...relationsData };
          })
        );
      }
    );

    return forkJoin(enrichedComponents).pipe(
      map((finalComponents: any[]) => {
        if (collectionKey) {
          return { ...data, [collectionKey]: finalComponents };
        }
        return finalComponents[0];
      })
    );
  }

  private extractRelationUrl(value: any): string | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      return value.trim() || null;
    }

    if (typeof value === 'object' && typeof value['@id'] === 'string') {
      return value['@id'].trim() || null;
    }

    return null;
  }

  private toAbsoluteApiUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) {
      return url;
    }

    return `${environment.apiUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  updateBlob(apiName: string, data: any): Observable<Blob> {
    const url = `${this.baseUrl}${apiName}`;
    return this.http
      .patch(url, data, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          if (!response.body) {
            throw new Error('Empty response body');
          }
          return response.body;
        })
      );
  }

  getBlob(apiName: string, data: any): Observable<Blob> {
    const url = `${this.baseUrl}${apiName}`;
    return this.http
      .get(url, {
        params: data,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          if (!response.body) {
            throw new Error('Empty response body');
          }
          return response.body;
        })
      );
  }
}
